import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cx } from "../cx";
import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "success" | "danger" | "ghost" | "link";
export type ButtonSize = "sm" | "md" | "lg" | "xl";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, disables the button and sets aria-busy (FE-01 FR-FE01-13). */
  pending?: boolean;
  /** Stretch to the container width (primary actions on auth screens, FE-05). */
  block?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-primary text-on-primary",
  secondary: "bg-transparent text-text shadow-[inset_0_0_0_1.5px_var(--sos-color-input-border)]",
  success: "bg-success text-on-primary",
  danger: "bg-danger text-on-primary",
  ghost: "bg-transparent text-text hover:bg-surface-muted",
  link: "bg-transparent px-1 text-primary underline underline-offset-2",
};

const SIZES: Record<ButtonSize, string> = {
  // sm: a 36 px pill whose hit area is still 48 px (FE-02 FR-FE02-06, C3): the ::after layer
  // extends the clickable box 6 px above and below.
  sm: "relative min-h-9 px-4 text-sm after:absolute after:inset-x-0 after:-inset-y-1.5 after:content-['']",
  md: "min-h-12 px-5",
  lg: "min-h-14 px-6 text-lg",
  // xl: drive-mode buttons, 72–84 px (FE-10).
  xl: "min-h-[72px] px-8 py-3 text-xl",
};

/** Pill button, 48 px minimum target (FE-02 §7.4); loading keeps its width and sets aria-busy. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    pending = false,
    block = false,
    disabled,
    className,
    children,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || pending}
      aria-busy={pending || undefined}
      className={cx(
        // Labels wrap instead of truncating (NFR-FE02-07, 200 % zoom and long translations).
        "inline-flex items-center justify-center gap-2 rounded-full text-center font-semibold [overflow-wrap:anywhere]",
        "transition-opacity disabled:cursor-not-allowed disabled:opacity-60",
        VARIANTS[variant],
        SIZES[size],
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {pending ? <Spinner size={18} /> : null}
      {children}
    </button>
  );
});
